/**
 * impact.mock.ts — Mock data for VER-59 Impact Scores tab.
 *
 * Shape contract:
 *   - WemwbsScores: WEMWBS pre/post pair + derived improvement delta.
 *   - MoodPoint: one S2–S5 session mood observation (aggregated — NO individual
 *     identifiers). Series is kept on a separate type intentionally so
 *     rendering code cannot accidentally overlay it on the WEMWBS axis
 *     (per JK spec: visually separate, different axis treatment).
 *
 * ANONYMITY NOTE (VER-59):
 *   - WemwbsScores contains NO participant identifiers. "participantRef" is an
 *     opaque, non-reversible label (e.g. "P1", "P2") used only for legend
 *     colouring inside this component. The backend MUST NOT return names,
 *     emails, or internal IDs to this surface.
 *   - MoodPoint uses sessionLabel (e.g. "S2") and aggregated groupMoodAvg.
 *     Individual mood values are never surfaced here.
 *   - Both types are suitable for group-level display only.
 *     When cohort size < 5 the backend should suppress scores entirely
 *     (k-anonymity guard — TODO [VER-55/VER-59]: enforce server-side).
 *
 * WIRES TO:
 *   - VER-55: real WEMWBS pre/post scores via SurveyMonkey API
 *   - VER-76: S2–S5 session mood line (mood captured per session)
 *
 * TODO [DATA]: Replace MOCK_WEMWBS_SCORES + MOCK_MOOD_SERIES with real API
 *   responses once VER-55 (pre/post scores) and VER-76 (mood series) are live.
 */

/** A single participant's anonymised WEMWBS pre/post pair. */
export type WemwbsScores = {
    /** Opaque display label — must NOT be a real name or re-identifying value. */
    participantRef: string;
    /** WEMWBS total score at programme start (range 14–70). */
    pre: number;
    /** WEMWBS total score at programme end (range 14–70). */
    post: number;
    /** post − pre; positive = improvement. Derived; stored for convenience. */
    delta: number;
};

/** One session's anonymised group mood observation (S2–S5). */
export type MoodPoint = {
    /** Session label, e.g. "S2", "S3". */
    sessionLabel: string;
    /** Mean mood score across all participants in that session (1–5 scale). */
    groupMoodAvg: number;
};

/** Aggregate summary derived from WemwbsScores[]. Pre-computed for display. */
export type WemwbsSummary = {
    meanPreScore: number;
    meanPostScore: number;
    meanDelta: number;
    improvedCount: number;
    totalCount: number;
};

// ---------------------------------------------------------------------------
// Mock WEMWBS pre/post data
// Realistic values: WEMWBS range 14–70; mean ~50 in healthy adult populations.
// ---------------------------------------------------------------------------

export const MOCK_WEMWBS_SCORES: WemwbsScores[] = [
    { participantRef: "P1", pre: 38, post: 51, delta: 13 },
    { participantRef: "P2", pre: 42, post: 55, delta: 13 },
    { participantRef: "P3", pre: 35, post: 44, delta: 9 },
    { participantRef: "P4", pre: 50, post: 58, delta: 8 },
    { participantRef: "P5", pre: 29, post: 40, delta: 11 },
    { participantRef: "P6", pre: 46, post: 49, delta: 3 },
    { participantRef: "P7", pre: 33, post: 48, delta: 15 },
    { participantRef: "P8", pre: 55, post: 61, delta: 6 },
];

/** Derived from MOCK_WEMWBS_SCORES. In production, compute server-side. */
export const MOCK_WEMWBS_SUMMARY: WemwbsSummary = (() => {
    const total = MOCK_WEMWBS_SCORES.length;
    const meanPre = Math.round(MOCK_WEMWBS_SCORES.reduce((acc, s) => acc + s.pre, 0) / total);
    const meanPost = Math.round(MOCK_WEMWBS_SCORES.reduce((acc, s) => acc + s.post, 0) / total);
    const meanDelta = Math.round(MOCK_WEMWBS_SCORES.reduce((acc, s) => acc + s.delta, 0) / total);
    const improvedCount = MOCK_WEMWBS_SCORES.filter((s) => s.delta > 0).length;
    return { meanPreScore: meanPre, meanPostScore: meanPost, meanDelta, improvedCount, totalCount: total };
})();

// ---------------------------------------------------------------------------
// Mock S2–S5 session mood series (VER-76)
// Scale 1–5; these are GROUP averages, not individual responses.
// ---------------------------------------------------------------------------

export const MOCK_MOOD_SERIES: MoodPoint[] = [
    { sessionLabel: "S2", groupMoodAvg: 2.8 },
    { sessionLabel: "S3", groupMoodAvg: 3.2 },
    { sessionLabel: "S4", groupMoodAvg: 3.7 },
    { sessionLabel: "S5", groupMoodAvg: 4.1 },
];
